import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, X, Shield, KeyRound, Sparkles, AlertCircle, Mail, ArrowRight } from 'lucide-react';
import { romanticAudio } from '../utils/audio';
import { signInAdmin } from '../services/authService';

interface PrivateLettersPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const PrivateLettersPinModal: React.FC<PrivateLettersPinModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setError(false);
      setErrorMessage('');
      setIsVerifying(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError(true);
      setErrorMessage('Por favor ingresa tu correo y contraseña.');
      return;
    }

    setIsVerifying(true);
    setError(false);
    setErrorMessage('');

    try {
      // Authoritative verification via Firebase Authentication + Custom Claims
      const result = await signInAdmin(email.trim(), password.trim());
      if (result.success && result.user) {
        romanticAudio.playBloomSuccess();
        onSuccess();
        onClose();
      } else {
        romanticAudio.playNote(220, 0.4, 'sawtooth', 0.1);
        setError(true);
        setErrorMessage(result.error || 'Credenciales no autorizadas o sin privilegios de administrador.');
      }
    } catch {
      romanticAudio.playNote(220, 0.4, 'sawtooth', 0.1);
      setError(true);
      setErrorMessage('Error al conectar con el servidor de autenticación.');
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            x: error ? [-8, 8, -6, 6, -3, 3, 0] : 0,
          }}
          transition={{ duration: error ? 0.4 : 0.3 }}
          className="relative w-full max-w-md bg-white border border-[#F0DDE4] rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden text-[#302B30]"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full text-[#77717A] hover:text-[#302B30] hover:bg-[#FFF4F7] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] mb-3 shadow-2xs">
              <Lock className="w-7 h-7" />
            </div>
            <h3 className="font-['Playfair_Display',serif] text-2xl font-bold text-[#302B30] tracking-tight">
              Bóveda Privada de Cartas
            </h3>
            <p className="text-xs text-[#77717A] mt-1 max-w-xs mx-auto">
              Ingresa tus credenciales autorizadas de pareja para desbloquear la colección íntima.
            </p>
          </div>

          {/* Auth Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-[#302B30] uppercase tracking-wider mb-1.5">
                Correo Electrónico
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#77717A]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="ejemplo@correo.com"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#F0DDE4] focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 rounded-xl text-[#302B30] placeholder-[#77717A]/50 text-xs sm:text-sm outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] font-bold text-[#302B30] uppercase tracking-wider">
                  Contraseña
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-[11px] font-semibold text-[#E83E78] hover:underline cursor-pointer"
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#77717A]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError(false);
                  }}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#F0DDE4] focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 rounded-xl text-[#302B30] placeholder-[#77717A]/50 text-xs sm:text-sm font-mono outline-none transition-all"
                />
              </div>
            </div>

            {/* Error Feedback */}
            {error && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifying}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 active:scale-95 disabled:opacity-50 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-pink-500/25 transition-all flex items-center justify-center gap-2 cursor-pointer mt-2"
            >
              <Shield className="w-4 h-4" />
              <span>{isVerifying ? 'Verificando credenciales...' : 'Desbloquear Bóveda'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Security Badge */}
          <div className="mt-5 pt-4 border-t border-[#F0DDE4] text-center flex items-center justify-center gap-2 text-[10px] text-[#77717A]">
            <Sparkles className="w-3.5 h-3.5 text-[#E83E78]" />
            <span>Acceso protegido y confidencial</span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
